const IS_DEV = process.env.NODE_ENV === "development";

function _log(...args) {
  if (IS_DEV) console.warn("[NT]", ...args);
}

const VALID_COLLECTIONS = new Set([
  "nt-students",
  "nt-videos",
  "nt-breakdowns",
  "nt-notes",
  "nt-chatlogs",
  "nt-activity-log",
  "nt-announcements",
  "nt-homework",
  "nt-friend-requests",
  "nt-assignments",
  "nt-prop-accounts",
  "nt-daily-setups",
]);

const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://nexustrades.net";

const DB = {
  _jwt: null,
  _csrf: null,
  _timeout: 15000,
  _base: API_BASE,
  setToken(jwt) {
    DB._jwt = jwt;
  },
  setCsrf(token) {
    DB._csrf = token;
    try {
      if (token) localStorage.setItem("nt_csrf", token);
      else localStorage.removeItem("nt_csrf");
    } catch (e) {
      _log("csrf persist failed:", e.name);
    }
  },
  _authHeaders() {
    const h = { "Content-Type": "application/json" };
    if (DB._jwt) h["Authorization"] = `Bearer ${DB._jwt}`;
    if (DB._csrf) h["X-CSRF-Token"] = DB._csrf;
    return h;
  },
  uploadHeaders() {
    const h = DB._jwt ? { Authorization: `Bearer ${DB._jwt}` } : {};
    if (DB._csrf) h["X-CSRF-Token"] = DB._csrf;
    return h;
  },
  async get(k) {
    if (!VALID_COLLECTIONS.has(k)) {
      _log("Invalid collection:", k);
      return null;
    }
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), DB._timeout);
      const res = await fetch(
        `${API_BASE}/api.php?action=get&col=${encodeURIComponent(k)}`,
        {
          headers: DB._authHeaders(),
          credentials: "include",
          signal: ctrl.signal,
        }
      );
      clearTimeout(t);
      if (res.status === 401 || res.status === 403) {
        _log("Auth rejected");
        if (res.status === 401 && typeof DB._onAuthFailure === "function")
          DB._onAuthFailure();
        return null;
      }
      if (res.status === 429) {
        _log("Rate limited");
        return null;
      }
      const items = await res.json();
      if (!items || !Array.isArray(items)) return null;
      if (items.length === 0) return [];
      items.sort((a, b) =>
        b.id && a.id ? b.id.localeCompare(a.id) : 0
      );
      return items;
    } catch (e) {
      _log("Read error:", e.name);
      return null;
    }
  },
  async set(k, v) {
    if (!VALID_COLLECTIONS.has(k)) {
      _log("Invalid collection:", k);
      return false;
    }
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), DB._timeout);
      const res = await fetch(
        `${API_BASE}/api.php?action=set&col=${encodeURIComponent(k)}`,
        {
          method: "POST",
          headers: DB._authHeaders(),
          body: JSON.stringify(v),
          credentials: "include",
          signal: ctrl.signal,
        }
      );
      clearTimeout(t);
      if (res.status === 401 || res.status === 403) {
        _log("Auth rejected");
        if (res.status === 401 && typeof DB._onAuthFailure === "function")
          DB._onAuthFailure();
        return false;
      }
      if (res.status === 429) {
        _log("Rate limited");
        return false;
      }
      const result = await res.json();
      return result.success === true;
    } catch (e) {
      _log("Write error:", e.name);
      return false;
    }
  },
  _onAuthFailure: null,
  async getLeaderboard() {
    try {
      const res = await fetch(
        `${API_BASE}/api.php?action=getLeaderboard`,
        {
          headers: DB._authHeaders(),
          credentials: "include",
        }
      );
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      _log("getLeaderboard error:", e.name);
      return null;
    }
  },
  async appendActivity(payload) {
    try {
      const res = await fetch(
        `${API_BASE}/api.php?action=appendActivity`,
        {
          method: "POST",
          headers: DB._authHeaders(),
          body: JSON.stringify(payload),
          credentials: "include",
        }
      );
      if (!res.ok) return false;
      const data = await res.json();
      return data.success === true;
    } catch (e) {
      _log("appendActivity error:", e.name);
      return false;
    }
  },
  async startVideo(videoId) {
    try {
      const res = await fetch(
        `${API_BASE}/api.php?action=startVideo`,
        {
          method: "POST",
          headers: DB._authHeaders(),
          body: JSON.stringify({ videoId }),
          credentials: "include",
        }
      );
      if (!res.ok) return false;
      const data = await res.json();
      return data.success === true;
    } catch (e) {
      _log("startVideo error:", e.name);
      return false;
    }
  },
  async markVideoWatched(videoId) {
    try {
      const res = await fetch(
        `${API_BASE}/api.php?action=markVideoWatched`,
        {
          method: "POST",
          headers: DB._authHeaders(),
          body: JSON.stringify({ videoId }),
          credentials: "include",
        }
      );
      if (!res.ok) return false;
      const data = await res.json();
      return data.success === true;
    } catch (e) {
      _log("markVideoWatched error:", e.name);
      return false;
    }
  },
  async updateProfile(patch) {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), DB._timeout);
      const res = await fetch(
        `${API_BASE}/api.php?action=updateProfile`,
        {
          method: "POST",
          headers: DB._authHeaders(),
          body: JSON.stringify(patch),
          credentials: "include",
          signal: ctrl.signal,
        }
      );
      clearTimeout(t);
      if (res.status === 401 || res.status === 403) {
        _log("Profile update rejected");
        return false;
      }
      if (res.status === 429) {
        _log("Rate limited");
        return false;
      }
      const result = await res.json();
      return result.success === true;
    } catch (e) {
      _log("Profile update error:", e.name);
      return false;
    }
  },
};

const SK = {
  students: "nt-students",
  videos: "nt-videos",
  breakdowns: "nt-breakdowns",
  notes: "nt-notes",
  chatlogs: "nt-chatlogs",
  activityLog: "nt-activity-log",
  announcements: "nt-announcements",
  homework: "nt-homework",
  friendRequests: "nt-friend-requests",
  assignments: "nt-assignments",
  propAccounts: "nt-prop-accounts",
  dailySetups: "nt-daily-setups",
};

const LEGACY_ADMIN_USER = "kev1827";

export { IS_DEV, _log, DB, SK, LEGACY_ADMIN_USER, VALID_COLLECTIONS };

